import type { TenantPrimaryKeys } from '@/configs/tenants/types';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useTenant } from '@/providers/TenantProvider';

const ORDER_SUMMARY_KEYS = [
  'customerName',
  'customerAddress',
  'customerCity',
  'lastOrderDate',
  'productName',
  'productDescription',
] as const;

export function useTenantConfiguration() {
  const { grains, grainValues, tenantConfig } = useTenant();
  const { state } = useForecastQueryState();
  const productInput = state.product || '';
  const shipFromInput = state.shipFrom || '';
  const shipToInput = state.customer || '';
  const shipToGeoInput = state.shipToGeography || '';

  const tenantCommonConfig = tenantConfig;
  const orderSummary = (tenantConfig.orderSummary as Record<string, string>) ?? {};
  const grainsApiFilter = (tenantConfig.grainsApiFilter as string[]) ?? [];
  const grainToInput: Record<string, string | undefined> = {
    [grains?.product ?? '']: productInput,
    [grains?.ship_from ?? '']: shipFromInput,
    [grains?.ship_to ?? '']: shipToInput,
    [grains?.ship_to_geography ?? '']: shipToGeoInput,
  };
  const tenantApiGrains = grainValues.filter((v) => !grainsApiFilter.includes(v));

  // API filters return all api filters with no customer logic
  const apiFilters = Object.fromEntries(
    Object.entries(grainToInput)
      .filter(([grainKey]) => grainKey)
      .map(([grainKey, value]) => [grainKey, value]),
  );

  // API Tenant filters account for some edge cases
  const tenantApiFilters = Object.fromEntries(
    Object.entries(grainToInput)
      .filter(([grainKey]) => grainKey && !grainsApiFilter.includes(grainKey))
      .map(([grainKey, value]) => [grainKey, value]),
  );

  function getTenantOrderSummaryData(data?: TenantPrimaryKeys) {
    const record = (data ?? {}) as unknown as Record<string, string>;

    return Object.fromEntries(
      ORDER_SUMMARY_KEYS.map((key) => [key, orderSummary[key] ? (record[orderSummary[key]] ?? '') : '']),
    ) as Record<(typeof ORDER_SUMMARY_KEYS)[number], string>;
  }

  return {
    getTenantOrderSummaryData,
    apiFilters,
    tenantConfig,
    tenantCommonConfig,
    tenantApiFilters,
    tenantApiGrains,
  };
}
